import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Sailboat, Sun, Anchor, Waves, Plus, Trash2, Users, Calendar, MapPin, ArrowRight, CheckCircle2, BadgeCheck, Info, Lock, CalendarPlus } from "lucide-react";
import { toast } from "sonner";
import { api, API, formatApiError } from "@/lib/api";
import { LOGO_URL, DEFAULT_BANNER, TAGLINE, GRADE_OPTIONS } from "@/lib/brand";
import BrandHeader from "@/components/BrandHeader";
import WaveDivider from "@/components/WaveDivider";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Checkbox } from "@/components/ui/checkbox";
import { Link } from "react-router-dom";

function formatPrettyDate(iso) {
  if (!iso) return "";
  try {
    const d = new Date(iso + "T00:00:00");
    return d.toLocaleDateString(undefined, {
      weekday: "long",
      month: "long",
      day: "numeric",
      year: "numeric",
    });
  } catch {
    return iso;
  }
}

function formatPrettyTime(t) {
  if (!t) return "";
  const [hh, mm] = t.split(":");
  const h = parseInt(hh, 10);
  const ampm = h >= 12 ? "PM" : "AM";
  const h12 = h % 12 || 12;
  return `${h12}:${mm} ${ampm}`;
}

// Build calendar quick-add URLs (zero-API; opens calendar web app pre-filled).
// Treats the event date/time as the parent's local time. Duration = 2 hours.
function _toCalDate(date, time, addHours = 0) {
  // date "YYYY-MM-DD", time "HH:MM" -> "YYYYMMDDTHHMMSS" (floating local time)
  const [y, m, d] = date.split("-").map(Number);
  const [hh, mm] = time.split(":").map(Number);
  const dt = new Date(y, m - 1, d, hh, mm + addHours * 60);
  const pad = (n) => String(n).padStart(2, "0");
  return `${dt.getFullYear()}${pad(dt.getMonth() + 1)}${pad(dt.getDate())}T${pad(dt.getHours())}${pad(dt.getMinutes())}00`;
}

function buildGoogleCalendarUrl(ev) {
  const start = _toCalDate(ev.date, ev.time);
  const end = _toCalDate(ev.date, ev.time, 2);
  const params = new URLSearchParams({
    action: "TEMPLATE",
    text: `Rolling River — ${ev.title}`,
    dates: `${start}/${end}`,
    details: ev.description || "Prospective Family Day at Rolling River Day Camp.",
    location: ev.location || "",
  });
  return `https://calendar.google.com/calendar/render?${params.toString()}`;
}

function buildOutlookCalendarUrl(ev) {
  // Outlook expects ISO format and treats it as the user's local time
  const toIso = (h = 0) => {
    const [y, m, d] = ev.date.split("-").map(Number);
    const [hh, mm] = ev.time.split(":").map(Number);
    const dt = new Date(y, m - 1, d, hh + h, mm);
    const pad = (n) => String(n).padStart(2, "0");
    return `${dt.getFullYear()}-${pad(dt.getMonth() + 1)}-${pad(dt.getDate())}T${pad(dt.getHours())}:${pad(dt.getMinutes())}:00`;
  };
  const params = new URLSearchParams({
    path: "/calendar/action/compose",
    rru: "addevent",
    subject: `Rolling River — ${ev.title}`,
    startdt: toIso(0),
    enddt: toIso(2),
    body: ev.description || "Prospective Family Day at Rolling River Day Camp.",
    location: ev.location || "",
  });
  return `https://outlook.live.com/calendar/0/deeplink/compose?${params.toString()}`;
}

function CustomFieldRenderer({ field, value, onChange }) {
  const label = field.label || "Question";
  const required = Boolean(field.required);
  const testid = `custom-field-${field.id}`;
  if (field.type === "textarea") {
    return (
      <div className="space-y-2">
        <Label className="text-sm font-bold text-[#1E3A8A]">
          {label} {required && <span className="text-red-500">*</span>}
        </Label>
        <Textarea
          data-testid={testid}
          value={value || ""}
          onChange={(e) => onChange(e.target.value)}
          placeholder={field.placeholder || ""}
          className="rounded-xl border-2 border-blue-100 focus:border-[#2F5FCB] min-h-[100px]"
        />
      </div>
    );
  }
  if (field.type === "select") {
    return (
      <div className="space-y-2">
        <Label className="text-sm font-bold text-[#1E3A8A]">
          {label} {required && <span className="text-red-500">*</span>}
        </Label>
        <Select value={value || ""} onValueChange={onChange}>
          <SelectTrigger data-testid={testid} className="rounded-xl border-2 border-blue-100 focus:border-[#2F5FCB] py-6 bg-white">
            <SelectValue placeholder="Select an option" />
          </SelectTrigger>
          <SelectContent className="rounded-xl">
            {(field.options || []).map((o) => (
              <SelectItem key={o} value={o}>{o}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>
    );
  }
  if (field.type === "checkbox") {
    return (
      <div className="flex items-start gap-3 bg-blue-50/40 rounded-2xl p-4 border border-blue-100">
        <Checkbox
          id={`cf-${field.id}`}
          data-testid={testid}
          checked={Boolean(value)}
          onCheckedChange={(v) => onChange(Boolean(v))}
          className="mt-0.5 h-5 w-5 border-2 border-blue-300 data-[state=checked]:bg-[#2F5FCB] data-[state=checked]:border-[#2F5FCB]"
        />
        <label htmlFor={`cf-${field.id}`} className="text-sm text-slate-700 leading-relaxed cursor-pointer select-none">
          {label} {required && <span className="text-red-500">*</span>}
        </label>
      </div>
    );
  }
  // default: text
  return (
    <div className="space-y-2">
      <Label className="text-sm font-bold text-[#1E3A8A]">
        {label} {required && <span className="text-red-500">*</span>}
      </Label>
      <Input
        data-testid={testid}
        value={value || ""}
        onChange={(e) => onChange(e.target.value)}
        placeholder={field.placeholder || ""}
        className="rounded-xl border-2 border-blue-100 focus:border-[#2F5FCB] py-3"
      />
    </div>
  );
}

export default function PublicForm() {
  const [events, setEvents] = useState([]);
  const [settings, setSettings] = useState({ banner_image: "", welcome_message: "", event_details: "", form_labels: {}, custom_fields: [] });
  const [loadingEvents, setLoadingEvents] = useState(true);
  const [selectedEventId, setSelectedEventId] = useState("");
  const [detailsOpen, setDetailsOpen] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [submittedEvent, setSubmittedEvent] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  const [parentNames, setParentNames] = useState("");
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [children, setChildren] = useState([{ name: "", age: "", grade: "" }]);
  const [adultCount, setAdultCount] = useState(1);
  const [adults, setAdults] = useState([{ name: "" }]);
  const [notes, setNotes] = useState("");
  const [smsOptOut, setSmsOptOut] = useState(false);
  const [customAnswers, setCustomAnswers] = useState({});

  const L = (key, fallback = "") => (settings.form_labels && settings.form_labels[key]) || fallback;
  const customFields = settings.custom_fields || [];

  useEffect(() => {
    Promise.all([api.get("/events"), api.get("/settings")])
      .then(([evRes, sRes]) => {
        setEvents(evRes.data);
        setSettings(sRes.data || {});
      })
      .catch(() => toast.error("Couldn't load events. Please refresh."))
      .finally(() => setLoadingEvents(false));
  }, []);

  // Keep adults array sized to adultCount
  useEffect(() => {
    setAdults((prev) => {
      const next = [...prev];
      while (next.length < adultCount) next.push({ name: "" });
      return next.slice(0, adultCount);
    });
  }, [adultCount]);

  const addChild = () => setChildren((c) => [...c, { name: "", age: "", grade: "" }]);
  const removeChild = (i) =>
    setChildren((c) => (c.length === 1 ? c : c.filter((_, idx) => idx !== i)));

  const updateChild = (i, field, val) =>
    setChildren((c) => c.map((row, idx) => (idx === i ? { ...row, [field]: val } : row)));

  const updateAdult = (i, val) =>
    setAdults((a) => a.map((row, idx) => (idx === i ? { name: val } : row)));

  const selectedEvent = events.find((e) => e.id === selectedEventId);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!selectedEventId) {
      toast.error("Please select an event date.");
      return;
    }
    if (!parentNames.trim()) {
      toast.error("Please enter the parent name(s).");
      return;
    }
    const first = children[0];
    if (!first.name.trim() || !first.age.trim() || !first.grade.trim()) {
      toast.error("First child's name, age, and grade are required.");
      return;
    }
    // Validate required custom fields
    for (const f of customFields) {
      if (!f.required) continue;
      const v = customAnswers[f.id];
      if (f.type === "checkbox") {
        if (!v) { toast.error(`Please check: "${f.label}"`); return; }
      } else {
        if (v == null || String(v).trim() === "") { toast.error(`Please answer: "${f.label}"`); return; }
      }
    }
    setSubmitting(true);
    try {
      await api.post("/inquiries", {
        event_id: selectedEventId,
        parent_names: parentNames.trim(),
        email: email.trim(),
        phone: phone.trim(),
        children: children
          .filter((c) => c.name.trim())
          .map((c) => ({ name: c.name.trim(), age: c.age.trim(), grade: c.grade.trim() })),
        adult_count: Number(adultCount) || 1,
        adults: adults.filter((a) => a.name.trim()).map((a) => ({ name: a.name.trim() })),
        notes: notes.trim(),
        sms_opt_out: smsOptOut,
        custom_answers: customAnswers,
      });
      setSubmitted(true);
      setSubmittedEvent(selectedEvent || null);
      toast.success("You're all set! See you at Parent Day.");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Submission failed");
    } finally {
      setSubmitting(false);
    }
  };

  const bannerSrc = settings.banner_image && settings.banner_image.trim() ? settings.banner_image : DEFAULT_BANNER;

  return (
    <div className="min-h-screen bg-white">
      <BrandHeader
        rightSlot={
          <Link
            to="/admin/login"
            data-testid="admin-link"
            className="inline-flex items-center gap-1.5 bg-[#2F5FCB] hover:bg-[#254BA0] text-white text-sm font-bold px-5 py-2.5 rounded-full transition-all hover:-translate-y-0.5 shadow-md shadow-blue-900/20"
          >
            <Lock className="h-3.5 w-3.5" /> Admin Login
          </Link>
        }
      />

      {/* HERO */}
      <section className="relative overflow-hidden">
        <div className="absolute inset-0 -z-10">
          <div className="absolute -top-20 -left-24 w-72 h-72 rounded-full bg-yellow-200/40 blur-3xl"></div>
          <div className="absolute top-32 -right-32 w-96 h-96 rounded-full bg-sky-200/50 blur-3xl"></div>
        </div>

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-12 md:pt-20 pb-16 grid lg:grid-cols-12 gap-10 items-center">
          <motion.div
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7 }}
            className="lg:col-span-6"
          >
            <div className="inline-flex items-center gap-2 bg-yellow-100 text-[#1E3A8A] px-4 py-1.5 rounded-full text-xs font-bold uppercase tracking-wider">
              <Sun className="h-4 w-4" />
              {TAGLINE}
            </div>
            <h1
              data-testid="hero-title"
              className="mt-5 font-display text-5xl md:text-6xl lg:text-7xl font-black text-[#1E3A8A] leading-[1.05] tracking-tight"
            >
              Prospective
              <span className="block text-[#2F5FCB]">Family Days</span>
            </h1>
            <p className="mt-6 text-lg md:text-xl text-slate-600 max-w-xl leading-relaxed font-medium">
              {settings.welcome_message ||
                "Embark on Life's Voyage — meet the Directors, tour our campus, and discover what makes Rolling River unforgettable for your family."}
            </p>
            <div className="mt-8 flex flex-wrap gap-3">
              <a
                href="#events"
                data-testid="hero-cta-events"
                className="inline-flex items-center gap-2 bg-[#2F5FCB] text-white rounded-full px-7 py-3.5 font-bold hover:-translate-y-1 transition-all duration-300 shadow-lg hover:shadow-[#2F5FCB]/30"
              >
                Select Your Event <ArrowRight className="h-4 w-4" />
              </a>
              <a
                href="#form"
                data-testid="hero-cta-form"
                className="inline-flex items-center gap-2 border-2 border-[#1E3A8A] text-[#1E3A8A] rounded-full px-7 py-3.5 font-bold hover:bg-blue-50 transition"
              >
                RSVP Now
              </a>
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.8, delay: 0.1 }}
            className="lg:col-span-6 relative"
          >
            <div className="relative">
              <img
                src={bannerSrc}
                alt="Rolling River campers"
                className="w-full h-[360px] md:h-[460px] object-cover rounded-[2.5rem] shadow-2xl shadow-blue-900/20"
                data-testid="hero-banner-image"
              />
              <div className="absolute -bottom-6 -left-6 bg-white rounded-2xl shadow-xl px-5 py-4 flex items-center gap-3 float-soft">
                <Sailboat className="h-8 w-8 text-[#2F5FCB]" />
                <div>
                  <div className="text-xs uppercase tracking-wider text-slate-500 font-bold">Est. 1992</div>
                  <div className="font-display text-lg font-bold text-[#1E3A8A]">35 Years Strong</div>
                </div>
              </div>
              <div className="absolute -top-6 -right-6 bg-yellow-300 rounded-full p-5 shadow-xl float-soft-delay">
                <Sun className="h-8 w-8 text-[#1E3A8A]" />
              </div>
            </div>
          </motion.div>
        </div>

        <WaveDivider color="#EFF6FF" />
      </section>

      {/* EVENTS */}
      <section id="events" className="bg-[#EFF6FF] pt-8 pb-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-end justify-between flex-wrap gap-3">
            <div>
              <div className="inline-flex items-center gap-2 text-[#2F5FCB] font-bold text-sm uppercase tracking-wider">
                <Calendar className="h-4 w-4" /> Choose a date
              </div>
              <h2 className="font-display text-3xl md:text-4xl font-bold text-[#1E3A8A] mt-2">
                Upcoming Prospective Family Days
              </h2>
            </div>
          </div>

          <div className="mt-8 grid sm:grid-cols-2 lg:grid-cols-3 gap-5" data-testid="events-list">
            {loadingEvents && (
              <div className="col-span-full text-center py-12 text-slate-500">Loading events…</div>
            )}
            {!loadingEvents && events.length === 0 && (
              <div className="col-span-full text-center py-16 bg-white rounded-3xl border-2 border-dashed border-blue-200">
                <Anchor className="h-10 w-10 mx-auto text-[#2F5FCB]" />
                <p className="mt-3 font-bold text-[#1E3A8A]">No events yet</p>
                <p className="text-sm text-slate-500">Check back soon — dates coming up!</p>
              </div>
            )}
            {events.map((ev) => {
              const active = ev.id === selectedEventId;
              const spotsLeft = Math.max(0, ev.capacity - (ev.registered_count || 0));
              const full = spotsLeft === 0;
              return (
                <motion.div
                  key={ev.id}
                  whileHover={{ y: -4 }}
                  data-testid={`event-card-${ev.id}`}
                  className={`text-left rounded-3xl p-6 transition-all border-2 flex flex-col ${
                    active
                      ? "bg-[#2F5FCB] text-white border-[#2F5FCB] shadow-xl shadow-[#2F5FCB]/30"
                      : "bg-white border-blue-100 hover:border-[#2F5FCB] shadow-sm"
                  } ${full ? "opacity-60" : ""}`}
                >
                  <div className="flex items-start justify-between">
                    <div className={`p-2 rounded-xl ${active ? "bg-white/20" : "bg-yellow-100"}`}>
                      <Sun className={`h-5 w-5 ${active ? "text-yellow-300" : "text-[#1E3A8A]"}`} />
                    </div>
                    {active && (
                      <BadgeCheck className="h-5 w-5 text-yellow-300" />
                    )}
                  </div>
                  <h3 className={`mt-4 font-display text-xl font-bold ${active ? "text-white" : "text-[#1E3A8A]"}`}>
                    {ev.title}
                  </h3>
                  <p className={`text-sm mt-1 ${active ? "text-blue-100" : "text-slate-600"}`}>
                    {formatPrettyDate(ev.date)}
                  </p>
                  <div className={`mt-4 space-y-1.5 text-sm ${active ? "text-blue-50" : "text-slate-600"}`}>
                    <div className="flex items-center gap-2">
                      <Calendar className="h-4 w-4" /> {formatPrettyTime(ev.time)}
                    </div>
                    <div className="flex items-center gap-2">
                      <MapPin className="h-4 w-4" /> {ev.location}
                    </div>
                    <div className="flex items-center gap-2">
                      <Users className="h-4 w-4" /> {full ? "Full" : `${spotsLeft} spots left`}
                    </div>
                  </div>
                  {ev.description && (
                    <p className={`mt-3 text-sm leading-relaxed ${active ? "text-blue-50" : "text-slate-500"}`}>
                      {ev.description}
                    </p>
                  )}

                  <div className="mt-5 pt-4 border-t border-current/10 flex flex-wrap gap-2">
                    <button
                      type="button"
                      data-testid={`select-event-${ev.id}`}
                      disabled={full}
                      onClick={() => {
                        if (full) return;
                        setSelectedEventId(ev.id);
                        document.getElementById("form")?.scrollIntoView({ behavior: "smooth" });
                      }}
                      className={`inline-flex items-center gap-1.5 px-4 py-2 rounded-full text-sm font-bold transition-all hover:-translate-y-0.5 disabled:cursor-not-allowed disabled:opacity-60 ${
                        active
                          ? "bg-yellow-300 text-[#1E3A8A] hover:bg-yellow-400"
                          : "bg-[#2F5FCB] text-white hover:bg-[#254BA0]"
                      }`}
                    >
                      {active ? "Selected" : (full ? "Full" : "Select")} {!active && !full && <ArrowRight className="h-3.5 w-3.5" />}
                    </button>
                    <button
                      type="button"
                      data-testid={`event-details-button-${ev.id}`}
                      onClick={() => setDetailsOpen(true)}
                      className={`inline-flex items-center gap-1.5 px-4 py-2 rounded-full text-sm font-bold border-2 transition-all hover:-translate-y-0.5 ${
                        active
                          ? "border-white/40 text-white hover:bg-white/10"
                          : "border-[#2F5FCB] text-[#2F5FCB] hover:bg-blue-50"
                      }`}
                    >
                      <Info className="h-3.5 w-3.5" /> Event Details
                    </button>
                  </div>
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>

      {/* FORM */}
      <section id="form" className="relative bg-white py-20">
        <div className="absolute inset-x-0 top-0 -translate-y-1 wave-divider h-12 md:h-20" style={{ transform: "scaleY(-1)" }}></div>
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
          <AnimatePresence mode="wait">
            {submitted ? (
              <motion.div
                key="success"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0 }}
                className="bg-white rounded-[2.5rem] border-2 border-green-100 shadow-xl p-10 text-center"
                data-testid="success-card"
              >
                <div className="mx-auto h-20 w-20 rounded-full bg-green-100 flex items-center justify-center">
                  <CheckCircle2 className="h-10 w-10 text-green-600" />
                </div>
                <h3 className="font-display text-3xl md:text-4xl font-bold text-[#1E3A8A] mt-6">
                  {L("success_heading", "Anchors away — you're in!")}
                </h3>
                <p className="text-slate-600 mt-3 max-w-md mx-auto">
                  {L("success_body", "Thanks for RSVPing! We can't wait to show you around Rolling River. We'll be in touch with directions and anything you need to bring.")}
                </p>

                {submittedEvent && (
                  <div className="mt-8 max-w-md mx-auto bg-blue-50 rounded-2xl p-5 text-left" data-testid="calendar-card">
                    <div className="flex items-center gap-2 text-[#1E3A8A] font-bold text-sm uppercase tracking-wider">
                      <CalendarPlus className="h-4 w-4" /> Add to your calendar
                    </div>
                    <p className="text-xs text-slate-600 mt-1">
                      Don&apos;t lose the date — save it now in your calendar app.
                    </p>
                    <div className="mt-4 grid grid-cols-1 sm:grid-cols-3 gap-2">
                      <a
                        data-testid="cal-ics-link"
                        href={`${API}/events/${submittedEvent.id}/calendar.ics`}
                        className="text-center inline-flex items-center justify-center gap-1.5 bg-[#2F5FCB] hover:bg-[#254BA0] text-white text-xs font-bold px-3 py-2.5 rounded-full transition-all hover:-translate-y-0.5"
                      >
                        Apple / Outlook
                      </a>
                      <a
                        data-testid="cal-google-link"
                        href={buildGoogleCalendarUrl(submittedEvent)}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-center inline-flex items-center justify-center gap-1.5 bg-white hover:bg-blue-50 text-[#1E3A8A] text-xs font-bold px-3 py-2.5 rounded-full transition-all hover:-translate-y-0.5 border-2 border-blue-100"
                      >
                        Google
                      </a>
                      <a
                        data-testid="cal-outlook-link"
                        href={buildOutlookCalendarUrl(submittedEvent)}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-center inline-flex items-center justify-center gap-1.5 bg-white hover:bg-blue-50 text-[#1E3A8A] text-xs font-bold px-3 py-2.5 rounded-full transition-all hover:-translate-y-0.5 border-2 border-blue-100"
                      >
                        Outlook Web
                      </a>
                    </div>
                  </div>
                )}
                <Button
                  data-testid="submit-another-button"
                  onClick={() => {
                    setSubmitted(false);
                    setSubmittedEvent(null);
                    setParentNames("");
                    setEmail("");
                    setPhone("");
                    setChildren([{ name: "", age: "", grade: "" }]);
                    setAdultCount(1);
                    setAdults([{ name: "" }]);
                    setNotes("");
                    setSmsOptOut(false);
                    setCustomAnswers({});
                    setSelectedEventId("");
                    window.scrollTo({ top: 0, behavior: "smooth" });
                  }}
                  className="mt-6 bg-[#2F5FCB] hover:bg-[#254BA0] text-white rounded-full px-7 py-3.5 font-bold"
                >
                  Submit another RSVP
                </Button>
              </motion.div>
            ) : (
              <motion.form
                key="form"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0 }}
                onSubmit={handleSubmit}
                className="bg-white rounded-[2.5rem] border border-blue-100 shadow-2xl shadow-blue-900/10 p-6 md:p-10 relative overflow-hidden"
                data-testid="inquiry-form"
              >
                <div className="absolute -top-10 -right-10 opacity-10">
                  <Waves className="h-40 w-40 text-[#2F5FCB]" />
                </div>

                <div className="relative">
                  <h2 className="font-display text-3xl md:text-4xl font-bold text-[#1E3A8A]">
                    {L("form_title", "Your RSVP")}
                  </h2>
                  <p className="text-slate-600 mt-2">
                    {selectedEvent ? (
                      <>Selected event: <span className="font-bold text-[#1E3A8A]">{selectedEvent.title}</span> · {formatPrettyDate(selectedEvent.date)}</>
                    ) : (
                      "Pick an event above, then fill out the form."
                    )}
                  </p>
                </div>

                {/* Parent */}
                <div className="mt-8 space-y-2">
                  <Label className="text-sm font-bold text-[#1E3A8A]">{L("parent_names_label", "Parent name(s)")} <span className="text-red-500">*</span></Label>
                  <Input
                    data-testid="parent-names-input"
                    value={parentNames}
                    onChange={(e) => setParentNames(e.target.value)}
                    placeholder={L("parent_names_placeholder", "e.g. Jane & John Smith")}
                    className="rounded-xl border-2 border-blue-100 focus:border-[#2F5FCB] py-3"
                    required
                  />
                </div>
                <div className="mt-4 grid sm:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label className="text-sm font-bold text-[#1E3A8A]">{L("email_label", "Email")}</Label>
                    <Input
                      data-testid="parent-email-input"
                      type="email"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      placeholder={L("email_placeholder", "you@example.com")}
                      className="rounded-xl border-2 border-blue-100 focus:border-[#2F5FCB] py-3"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label className="text-sm font-bold text-[#1E3A8A]">{L("phone_label", "Phone")}</Label>
                    <Input
                      data-testid="parent-phone-input"
                      value={phone}
                      onChange={(e) => setPhone(e.target.value)}
                      placeholder={L("phone_placeholder", "(555) 555-5555")}
                      className="rounded-xl border-2 border-blue-100 focus:border-[#2F5FCB] py-3"
                    />
                  </div>
                </div>

                {/* Children */}
                <div className="mt-10">
                  <div className="flex items-center justify-between">
                    <h3 className="font-display text-2xl font-bold text-[#1E3A8A]">{L("children_section_title", "Your Children")}</h3>
                    <button
                      type="button"
                      data-testid="add-child-button"
                      onClick={addChild}
                      className="inline-flex items-center gap-1.5 bg-yellow-300 hover:bg-yellow-400 text-[#1E3A8A] text-sm font-bold px-4 py-2 rounded-full transition-all hover:-translate-y-0.5"
                    >
                      <Plus className="h-4 w-4" /> {L("add_child_button", "Add another child")}
                    </button>
                  </div>

                  <div className="mt-4 space-y-4">
                    {children.map((child, i) => {
                      const required = i === 0;
                      return (
                        <div
                          key={i}
                          className="bg-blue-50/60 rounded-2xl p-4 md:p-5 relative"
                          data-testid={`child-row-${i}`}
                        >
                          <div className="flex items-center justify-between mb-3">
                            <div className="text-sm font-bold text-[#1E3A8A] flex items-center gap-2">
                              <span className="h-7 w-7 rounded-full bg-[#2F5FCB] text-white inline-flex items-center justify-center text-xs">
                                {i + 1}
                              </span>
                              Child #{i + 1} {required && <span className="text-red-500">*</span>}
                            </div>
                            {i > 0 && (
                              <button
                                type="button"
                                data-testid={`remove-child-${i}`}
                                onClick={() => removeChild(i)}
                                className="text-red-500 hover:text-red-700 p-1.5 rounded-full hover:bg-red-50 transition"
                                aria-label="Remove child"
                              >
                                <Trash2 className="h-4 w-4" />
                              </button>
                            )}
                          </div>
                          <div className="grid sm:grid-cols-3 gap-3">
                            <Input
                              data-testid={`child-name-${i}`}
                              value={child.name}
                              onChange={(e) => updateChild(i, "name", e.target.value)}
                              placeholder={L("child_name_placeholder", "Child's name")}
                              className="rounded-xl border-2 border-blue-100 focus:border-[#2F5FCB] bg-white py-3"
                              required={required}
                            />
                            <Input
                              data-testid={`child-age-${i}`}
                              value={child.age}
                              onChange={(e) => updateChild(i, "age", e.target.value)}
                              placeholder={L("child_age_placeholder", "Age")}
                              type="number"
                              min="0"
                              max="18"
                              className="rounded-xl border-2 border-blue-100 focus:border-[#2F5FCB] bg-white py-3"
                              required={required}
                            />
                            <Select
                              value={child.grade}
                              onValueChange={(v) => updateChild(i, "grade", v)}
                            >
                              <SelectTrigger
                                data-testid={`child-grade-${i}`}
                                className="rounded-xl border-2 border-blue-100 focus:border-[#2F5FCB] bg-white py-6"
                              >
                                <SelectValue placeholder={L("child_grade_placeholder", "Grade going into next fall")} />
                              </SelectTrigger>
                              <SelectContent className="rounded-xl">
                                {GRADE_OPTIONS.map((g) => (
                                  <SelectItem key={g} value={g}>
                                    {g}
                                  </SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>

                {/* Adults */}
                <div className="mt-10">
                  <h3 className="font-display text-2xl font-bold text-[#1E3A8A]">{L("adults_section_title", "Adults Attending")}</h3>
                  <p className="text-sm text-slate-600 mt-1">{L("adults_section_subtitle", "How many adults will be coming with you?")}</p>

                  <div className="mt-4 flex items-center gap-3">
                    <button
                      type="button"
                      data-testid="adult-count-decrement"
                      onClick={() => setAdultCount((n) => Math.max(1, n - 1))}
                      className="h-10 w-10 rounded-full bg-blue-100 text-[#1E3A8A] font-bold hover:bg-blue-200 transition"
                    >
                      –
                    </button>
                    <Input
                      data-testid="adult-count-input"
                      type="number"
                      min="1"
                      max="10"
                      value={adultCount}
                      onChange={(e) => setAdultCount(Math.max(1, Math.min(10, parseInt(e.target.value) || 1)))}
                      className="w-20 text-center rounded-xl border-2 border-blue-100 focus:border-[#2F5FCB] py-3 font-bold text-lg"
                    />
                    <button
                      type="button"
                      data-testid="adult-count-increment"
                      onClick={() => setAdultCount((n) => Math.min(10, n + 1))}
                      className="h-10 w-10 rounded-full bg-blue-100 text-[#1E3A8A] font-bold hover:bg-blue-200 transition"
                    >
                      +
                    </button>
                    <span className="text-sm text-slate-500 font-medium">adult(s)</span>
                  </div>

                  <div className="mt-5 grid sm:grid-cols-2 gap-3">
                    {adults.map((a, i) => (
                      <Input
                        key={i}
                        data-testid={`adult-name-${i}`}
                        value={a.name}
                        onChange={(e) => updateAdult(i, e.target.value)}
                        placeholder={`${L("adult_name_placeholder", "Adult name")} ${i + 1}`}
                        className="rounded-xl border-2 border-blue-100 focus:border-[#2F5FCB] py-3"
                      />
                    ))}
                  </div>
                </div>

                {/* Notes */}
                <div className="mt-10 space-y-2">
                  <Label className="text-sm font-bold text-[#1E3A8A]">{L("notes_label", "Anything else we should know?")}</Label>
                  <Textarea
                    data-testid="notes-input"
                    value={notes}
                    onChange={(e) => setNotes(e.target.value)}
                    placeholder={L("notes_placeholder", "Allergies, accessibility, questions, dietary…")}
                    className="rounded-xl border-2 border-blue-100 focus:border-[#2F5FCB] min-h-[100px]"
                  />
                </div>

                {/* Custom fields (admin-defined) */}
                {customFields.length > 0 && (
                  <div className="mt-10 space-y-5">
                    {customFields.map((f) => (
                      <CustomFieldRenderer
                        key={f.id}
                        field={f}
                        value={customAnswers[f.id]}
                        onChange={(v) => setCustomAnswers((prev) => ({ ...prev, [f.id]: v }))}
                      />
                    ))}
                  </div>
                )}

                {/* SMS consent */}
                <div className="mt-10 bg-sky-50/60 rounded-2xl p-5 border border-blue-100">
                  <div className="flex items-start gap-3">
                    <Checkbox
                      id="sms-opt-out"
                      data-testid="sms-opt-out-checkbox"
                      checked={smsOptOut}
                      onCheckedChange={(v) => setSmsOptOut(Boolean(v))}
                      className="mt-1 h-5 w-5 border-2 border-blue-300 data-[state=checked]:bg-[#2F5FCB] data-[state=checked]:border-[#2F5FCB]"
                    />
                    <label htmlFor="sms-opt-out" className="text-sm text-slate-700 leading-relaxed cursor-pointer select-none">
                      <span className="font-bold text-[#1E3A8A]">{L("sms_consent_title", "No thanks — don't text me.")}</span>{" "}
                      {L("sms_consent_body", "Check this box if you do not want SMS reminders.")}
                    </label>
                  </div>
                  <p className="mt-3 text-xs text-slate-500 leading-relaxed">
                    By submitting this form with a phone number (and leaving the box above <em>unchecked</em>), you consent to
                    receive a confirmation text and reminder texts about your Prospective Family Day visit from Rolling River
                    Day Camp. Message and data rates may apply. Message frequency varies (up to 3 messages per RSVP). Reply
                    <strong> STOP</strong> at any time to opt out, or <strong>HELP</strong> for help. Consent is not a
                    condition of attending.
                  </p>
                </div>

                {/* Submit */}
                <div className="mt-10 flex flex-wrap items-center gap-4">
                  <Button
                    type="submit"
                    data-testid="submit-inquiry-button"
                    disabled={submitting}
                    className="bg-yellow-300 hover:bg-yellow-400 text-[#1E3A8A] rounded-full px-10 py-6 font-bold text-base shadow-lg hover:-translate-y-1 transition-all"
                  >
                    {submitting ? "Submitting…" : L("submit_button", "Submit RSVP")}
                    <Sailboat className="ml-2 h-5 w-5" />
                  </Button>
                  <p className="text-xs text-slate-500 max-w-xs">
                    {L("form_footer_note", "By submitting, you agree to be contacted by Rolling River about Prospective Family Days.")}
                  </p>
                </div>
              </motion.form>
            )}
          </AnimatePresence>
        </div>
      </section>

      {/* Event Details Dialog */}
      <Dialog open={detailsOpen} onOpenChange={setDetailsOpen}>
        <DialogContent
          className="rounded-3xl max-w-2xl max-h-[85vh] overflow-y-auto p-0"
          data-testid="event-details-dialog"
        >
          <div className="bg-gradient-to-br from-sky-100 to-blue-50 px-6 py-5 border-b border-blue-100">
            <DialogHeader className="text-left space-y-1">
              <div className="inline-flex items-center gap-2 bg-yellow-200 text-[#1E3A8A] px-3 py-1 rounded-full text-xs font-bold uppercase tracking-wider w-fit">
                <Sun className="h-3.5 w-3.5" /> Overview
              </div>
              <DialogTitle className="font-display text-3xl font-bold text-[#1E3A8A]">
                Prospective Family Day
              </DialogTitle>
            </DialogHeader>
          </div>
          <div className="px-6 py-6">
            <div
              data-testid="event-details-content"
              className="text-slate-700 leading-relaxed whitespace-pre-line text-[15px] space-y-0"
            >
              {settings.event_details}
            </div>
          </div>
          <div className="px-6 py-4 border-t border-blue-50 bg-white sticky bottom-0 flex justify-end">
            <Button
              data-testid="event-details-close"
              onClick={() => setDetailsOpen(false)}
              className="bg-[#2F5FCB] hover:bg-[#254BA0] text-white rounded-full px-6 font-bold"
            >
              Got it
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Footer */}
      <footer className="bg-[#1E3A8A] text-blue-100 py-10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 flex flex-col md:flex-row items-center justify-between gap-4">
          <div className="flex items-center gap-3">
            <img src={LOGO_URL} alt="Rolling River" className="h-10 w-10 bg-white rounded-full" />
            <div>
              <div className="font-display font-bold text-white">Rolling River Day Camp</div>
              <div className="text-xs">Est. 1992 · {TAGLINE}</div>
            </div>
          </div>
          <div className="text-xs">© {new Date().getFullYear()} Rolling River Day Camp. All rights reserved.</div>
        </div>
      </footer>
    </div>
  );
}
